import { useCallback, useEffect, useMemo, useSyncExternalStore } from "react"
import { listCustomersForFacilitator } from "@/services/customers"
import { listSessionsForFacilitator } from "@/services/sessions"
import { useAuthStore, selectAuthUid } from "@/store/authStore"
import { describeWriteError } from "@/utils/firestoreWrite"
import { byRemovedAt, isRemoved, sessionRemoval } from "@/utils/removal"
import type { RemovalState } from "@/utils/removal"
import { byCreatedAt } from "@/utils/timestamps"
import type { Customer, Session } from "@/types"

/**
 * Every customer and session the signed-in facilitator may see.
 *
 * One-shot reads, not listeners: these lists back navigation and index screens,
 * where a row appearing under the cursor is worse than a row being one reload
 * stale. `sessionSync` is still the only thing that wires listeners into the
 * store, and this deliberately does not touch the store at all.
 *
 * `listSessionsForFacilitator` is the one `array-contains facilitatorIds` query
 * the rules permit, and it already sorts newest-first on the client — never
 * `orderBy` on a server-stamped field, which would omit the newest document,
 * and never `limit()`, which would break the counts. Anything narrower than "my
 * sessions" is filtered by whoever calls this.
 *
 * **Held once per uid, not once per mount.** `AppShell` carries the nav on every
 * facilitator screen and is remounted by every navigation, so a per-mount read
 * meant both queries ran again on every hop — including on the screens that want
 * nothing but the nav (the deck, a report, a customer) and never read the lists
 * at all. On an account with a few hundred sessions that is an unbounded scan
 * per click, and it grows with the history.
 *
 * The store below is what the comment here used to refuse, and the refusal was
 * right about the risk: a cache nobody invalidates goes stale, and a nav menu
 * offering a customer renamed ten minutes ago costs more than the extra read. So
 * this is not a cache with a lifetime. A mount that finds the entry older than
 * `STALE_AFTER_MS` revalidates it, and `reload()` forces a read outright — what
 * is removed is the SECOND read inside one piece of work, not the refresh.
 */

const STALE_AFTER_MS = 30_000

/**
 * One archived or deleted session, with the fact the Archive has to render:
 * whether it was filed away on its own or came along with its customer.
 */
export interface ArchivedSession {
  session: Session
  state: Exclude<RemovalState, "active">
  /** Hidden by its customer's removal, not its own. No Restore of its own. */
  inherited: boolean
  /** The customer that took it, when that is why it is here. */
  customer: Customer | null
  /**
   * Its customer is removed too — whether or not that is why this row is here.
   * A self-removed session under a removed customer would come back to a list it
   * is still hidden from, so it has no Restore either. Wider than `inherited`,
   * which is false for exactly that row.
   */
  customerRemoved: boolean
}

export interface FacilitatorIndex {
  /**
   * The WORKING lists: what is neither archived nor deleted.
   *
   * Every screen that shows customers or sessions reads these two, which is what
   * makes one archive write empty the row out of the grid, the table, both nav
   * menus and the search at once. The services return everything — the rules
   * admit one query per collection — so this is the only place the split is
   * made, and no screen can forget to make it.
   *
   * A session is dropped when its CUSTOMER is removed, without anything having
   * been written to the session. See `utils/removal.sessionRemoval`.
   */
  customers: Customer[] | null
  sessions: Session[] | null
  /** What the Archive screen renders. Null exactly when the lists above are. */
  archivedCustomers: Customer[] | null
  archivedSessions: ArchivedSession[] | null
  error: string | null
  /**
   * A RELOAD is in flight, not the first read.
   *
   * The first read is already legible — the lists are null and the screen says
   * so — and raising a flag for it would mean a render that says "loading"
   * before the one that says "empty". This is here so the Try again button can
   * say it is trying.
   */
  loading: boolean
  reload: () => void
}

/*
 * What the store holds: the lists as they came back, archived rows and all. The
 * split into working and archived is derived per render below rather than stored
 * twice — there is only one source, and two copies could disagree about which
 * list a row belongs in.
 */
interface Snapshot {
  customers: Customer[] | null
  sessions: Session[] | null
  error: string | null
  loading: boolean
}

const EMPTY: Snapshot = { customers: null, sessions: null, error: null, loading: false }

interface Entry {
  /** Replaced wholesale, never mutated: `useSyncExternalStore` compares identity. */
  snapshot: Snapshot
  loadedAt: number
  inFlight: boolean
  /** A forced read that arrived mid-flight, to run once this one lands. */
  queued: { announce: boolean } | null
  listeners: Set<() => void>
}

/* Keyed by uid, so signing in as someone else cannot show their predecessor's
   lists — the rules would refuse them anyway, but the screen would have already
   drawn them. */
const entries = new Map<string, Entry>()

function entryFor(uid: string): Entry {
  const existing = entries.get(uid)
  if (existing) return existing
  const created: Entry = {
    snapshot: EMPTY,
    loadedAt: 0,
    inFlight: false,
    queued: null,
    listeners: new Set(),
  }
  entries.set(uid, created)
  return created
}

function publish(entry: Entry, next: Partial<Snapshot>): void {
  entry.snapshot = { ...entry.snapshot, ...next }
  for (const listener of entry.listeners) listener()
}

/**
 * Marks what is held as out of date, without reading anything.
 *
 * Called by the screens that create or rename a customer, which is the case the
 * staleness window would otherwise show wrong: the write lands, the facilitator
 * navigates, and the nav menu offers the name they just changed. Not a reload,
 * deliberately — a read fired straight after a write races it, and there is
 * nothing on screen yet that needs the answer. The next mount does the reading.
 */
export function invalidateFacilitatorIndex(): void {
  for (const entry of entries.values()) entry.loadedAt = 0
}

/**
 * `force` is the difference between "somebody needs these lists" and "somebody
 * needs these lists AS OF NOW". A mount joins a read already running; a reload
 * fired after a write cannot, because that read started before the write and is
 * guaranteed to publish the row the caller just removed. So it queues instead of
 * returning, and the answer it wanted arrives one round trip later.
 */
function load(uid: string, { announce, force }: { announce: boolean; force: boolean }): void {
  const entry = entryFor(uid)
  // Two screens mounting in the same tick ask once. Without this, a shell and
  // the screen inside it would each start their own pair of queries.
  if (entry.inFlight) {
    if (!force) return
    entry.queued = { announce: (entry.queued?.announce ?? false) || announce }
    if (announce) publish(entry, { loading: true })
    return
  }
  entry.inFlight = true
  if (announce) publish(entry, { loading: true })

  Promise.all([listCustomersForFacilitator(uid), listSessionsForFacilitator(uid)])
    .then(([customers, sessions]) => {
      entry.loadedAt = Date.now()
      publish(entry, { customers: [...customers].sort(byCreatedAt), sessions, error: null })
    })
    .catch((caught: unknown) => publish(entry, { error: describeWriteError(caught) }))
    .finally(() => {
      entry.inFlight = false
      const queued = entry.queued
      entry.queued = null
      // `loading` stays raised across the handover: the work the flag describes
      // is not over, and lowering it here would blink Try again back to rest.
      if (queued) {
        load(uid, { announce: queued.announce, force: true })
        return
      }
      publish(entry, { loading: false })
    })
}

export function useFacilitatorIndex(): FacilitatorIndex {
  const uid = useAuthStore(selectAuthUid)

  const subscribe = useCallback(
    (onChange: () => void) => {
      if (!uid) return () => {}
      const entry = entryFor(uid)
      entry.listeners.add(onChange)
      return () => {
        entry.listeners.delete(onChange)
      }
    },
    [uid]
  )

  const snapshot = useSyncExternalStore(subscribe, () => (uid ? entryFor(uid).snapshot : EMPTY))
  const split = useMemo(() => partition(snapshot.customers, snapshot.sessions), [snapshot])

  useEffect(() => {
    if (!uid) return
    const entry = entryFor(uid)
    // Silent: this is the first read this screen has seen, and the null lists
    // already say so. `loading` belongs to Try again.
    if (entry.snapshot.customers === null || Date.now() - entry.loadedAt > STALE_AFTER_MS) {
      load(uid, { announce: false, force: false })
    }
  }, [uid])

  const reload = useCallback(() => {
    // Nothing to read without a uid, and `load` returns immediately — raising the
    // flag anyway leaves Try again spinning forever with no request in flight.
    if (uid) load(uid, { announce: true, force: true })
  }, [uid])

  return { ...snapshot, ...split, reload }
}

/**
 * Working rows on one side, the Archive on the other.
 *
 * Derived, not stored, and derived HERE rather than on each screen: a customer
 * archived on the customer screen has to vanish from the nav menus and the
 * search in the same render, and those read this hook too.
 *
 * The customers are indexed first because a session's answer depends on its
 * customer's — one pass each rather than a scan per session, which on a
 * facilitator with a few hundred sessions is the difference between linear and
 * quadratic on every navigation.
 */
function partition(
  customers: Customer[] | null,
  sessions: Session[] | null
): Pick<FacilitatorIndex, "customers" | "sessions" | "archivedCustomers" | "archivedSessions"> {
  if (customers === null || sessions === null) {
    return {
      customers: null,
      sessions: null,
      archivedCustomers: null,
      archivedSessions: null,
    }
  }

  const byId = new Map(customers.map((customer) => [customer.id, customer]))
  const archivedSessions: ArchivedSession[] = []
  const workingSessions: Session[] = []

  for (const session of sessions) {
    // A session whose customer this facilitator cannot read resolves to null and
    // is treated as present — see `sessionRemoval`.
    const customer = byId.get(session.customerId) ?? null
    const { state, inherited } = sessionRemoval(session, customer)
    if (state === "active") {
      workingSessions.push(session)
    } else {
      archivedSessions.push({
        session,
        state,
        inherited,
        customer: inherited ? customer : null,
        customerRemoved: isRemoved(customer),
      })
    }
  }

  /* Sorted by when they were removed, not when they were created: the Archive is
     read as "what did I just put here", and a session created last year and
     filed this morning belongs at the top. An inherited row has no removal stamp
     of its own, so it sorts by its customer's. */
  archivedSessions.sort((a, b) =>
    byRemovedAt(
      a.inherited ? (a.customer ?? a.session) : a.session,
      b.inherited ? (b.customer ?? b.session) : b.session
    )
  )

  return {
    customers: customers.filter((customer) => !isRemoved(customer)),
    sessions: workingSessions,
    archivedCustomers: customers.filter(isRemoved).sort(byRemovedAt),
    archivedSessions,
  }
}
